<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue';
import QRCode from 'qrcode';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAccount } from 'dashboard/composables/useAccount';
import waCallsApi from 'dashboard/api/waCallsApi';

const props = defineProps({
  inboxId: { type: Number, required: true },
});

const { t } = useI18n();
const { accountId } = useAccount();
const store = useStore();

const loading = ref(true);
const actionLoading = ref(false);
const sessions = ref([]);
const error = ref('');
const qrData = ref({});
const newSessionName = ref('');
const showAddForm = ref(false);

const chatwootConfigs = ref({});
let sseSource = null;
const autoPairedSids = new Set();

const wacallsUrl = computed(
  () => window.chatwootConfig?.WACALLS_URL || 'http://localhost:8080'
);

const linkedSessionId = computed(() => {
  const inbox = store.getters['inboxes/getInbox'](props.inboxId);
  return inbox?.additional_attributes?.wacalls_session_id || null;
});

const visibleSessions = computed(() => {
  if (!linkedSessionId.value) return sessions.value;
  return sessions.value.filter(s => s.id === linkedSessionId.value);
});

const startQRListener = sidOrSids => {
  if (sseSource) sseSource.close();
  const watchSids = Array.isArray(sidOrSids) ? sidOrSids : [sidOrSids];

  const apiKey = window.chatwootConfig?.WACALLS_API_KEY || '';
  const base = (
    window.chatwootConfig?.WACALLS_URL || 'http://localhost:8080'
  ).replace(/\/$/, '');
  const url = `${base}/api/events${
    apiKey ? `?apiKey=${encodeURIComponent(apiKey)}` : ''
  }`;
  sseSource = new EventSource(url);
  sseSource.onmessage = evt => {
    try {
      const msg = JSON.parse(evt.data);
      const evtSid = msg.sessionId || msg.session;
      if (
        (msg.type === 'session-qr' || msg.type === 'auth-state') &&
        msg.qr &&
        watchSids.includes(evtSid)
      ) {
        qrData.value = { ...qrData.value, [evtSid]: msg.qr };
      }
      if (msg.type === 'session-list') {
        let allOpen = true;
        watchSids.forEach(watchedSid => {
          const updated = (msg.sessions || []).find(s => s.id === watchedSid);
          if (updated?.state === 'open') {
            qrData.value = { ...qrData.value, [watchedSid]: null };
            autoPairedSids.delete(watchedSid);
          } else {
            allOpen = false;
          }
        });
        if (allOpen) {
          sseSource.close();
          sseSource = null;
          // eslint-disable-next-line no-use-before-define
          fetchSessions();
        }
      }
    } catch {
      // ignore malformed SSE data
    }
  };
  sseSource.onerror = () => {
    sseSource.close();
    sseSource = null;
  };
};

const fetchSessions = async () => {
  try {
    loading.value = true;
    error.value = '';
    const data = await waCallsApi.getSessions(accountId.value);
    sessions.value = Array.isArray(data) ? data : data.sessions || [];
    const qrSessions = visibleSessions.value.filter(s => s.state === 'qr');
    if (qrSessions.length > 0) {
      const sids = qrSessions.map(s => s.id);
      if (!sseSource) startQRListener(sids);
      qrSessions.forEach(sessionItem => {
        if (
          !qrData.value[sessionItem.id] &&
          !autoPairedSids.has(sessionItem.id)
        ) {
          autoPairedSids.add(sessionItem.id);
          waCallsApi
            .pairSession(accountId.value, sessionItem.id)
            .then(result => {
              if (result?.qr)
                qrData.value = { ...qrData.value, [sessionItem.id]: result.qr };
            })
            .catch(() => {});
        }
      });
    }
  } catch (e) {
    error.value = e?.response?.data?.error || e.message;
  } finally {
    loading.value = false;
  }
};

const createSession = async () => {
  if (!newSessionName.value.trim()) return;
  try {
    actionLoading.value = true;
    await waCallsApi.createSession(accountId.value, {
      name: newSessionName.value.trim(),
    });
    newSessionName.value = '';
    showAddForm.value = false;
    await fetchSessions();
  } catch (e) {
    error.value = e?.response?.data?.error || e.message;
  } finally {
    actionLoading.value = false;
  }
};

const renderQR = async (sid, text) => {
  await nextTick();
  const canvas = document.getElementById(`qr-${sid}`);
  if (canvas && text) {
    await QRCode.toCanvas(canvas, text, { width: 220, margin: 1 });
  }
};

watch(
  qrData,
  val => {
    Object.entries(val).forEach(([sid, text]) => {
      if (text) renderQR(sid, text);
    });
  },
  { deep: true }
);

const pairSession = async sid => {
  try {
    actionLoading.value = true;
    error.value = '';
    autoPairedSids.delete(sid);
    startQRListener(sid);
    const result = await waCallsApi.pairSession(accountId.value, sid);
    if (result?.qr || result?.data) {
      qrData.value = { ...qrData.value, [sid]: result.qr || result.data };
    }
    await fetchSessions();
  } catch (e) {
    error.value = e?.response?.data?.error || e.message;
  } finally {
    actionLoading.value = false;
  }
};

const logoutSession = async sid => {
  try {
    actionLoading.value = true;
    await waCallsApi.logoutSession(accountId.value, sid);
    qrData.value = { ...qrData.value, [sid]: null };
    await fetchSessions();
  } catch (e) {
    error.value = e?.response?.data?.error || e.message;
  } finally {
    actionLoading.value = false;
  }
};

const autoConfigureChatwoot = async sid => {
  try {
    actionLoading.value = true;
    error.value = '';
    const data = await waCallsApi.autoConfigure(
      accountId.value,
      sid,
      props.inboxId
    );
    chatwootConfigs.value = { ...chatwootConfigs.value, [sid]: data };
    if (data?.webhook_url && props.inboxId) {
      await store.dispatch('inboxes/updateInbox', {
        id: props.inboxId,
        formData: false,
        channel: { webhook_url: data.webhook_url },
      });
    }
  } catch (e) {
    error.value = e?.response?.data?.error || e.message;
  } finally {
    actionLoading.value = false;
  }
};

const deleteSession = async sid => {
  autoPairedSids.delete(sid);
  if (!window.confirm(t('INBOX_MGMT.WACALLS_SETTINGS.DELETE_CONFIRM'))) return;
  try {
    actionLoading.value = true;
    await waCallsApi.deleteSession(accountId.value, sid);
    await fetchSessions();
  } catch (e) {
    error.value = e?.response?.data?.error || e.message;
  } finally {
    actionLoading.value = false;
  }
};

const statusColor = state => {
  switch (state) {
    case 'open':
      return 'text-green-600 dark:text-green-400';
    case 'qr':
    case 'connecting':
      return 'text-blue-600 dark:text-blue-400';
    case 'logged_out':
      return 'text-red-500 dark:text-red-400';
    default:
      return 'text-yellow-600 dark:text-yellow-400';
  }
};

const statusBg = state => {
  switch (state) {
    case 'open':
      return 'bg-green-100 dark:bg-green-900/30';
    case 'qr':
    case 'connecting':
      return 'bg-blue-100 dark:bg-blue-900/30';
    case 'logged_out':
      return 'bg-red-100 dark:bg-red-900/30';
    default:
      return 'bg-slate-100 dark:bg-slate-700';
  }
};

let pollingInterval = null;

onMounted(async () => {
  await fetchSessions();
  pollingInterval = setInterval(fetchSessions, 10000);
});

onUnmounted(() => {
  if (pollingInterval) clearInterval(pollingInterval);
  if (sseSource) sseSource.close();
});
</script>

<template>
  <div
    class="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden"
  >
    <div
      class="flex items-center justify-between px-5 py-4 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700"
    >
      <div class="flex items-center gap-2">
        <svg
          viewBox="0 0 24 24"
          class="size-5 text-green-600"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.8 1.24h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 6 6l.96-.96a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"
          />
        </svg>
        <h3 class="text-base font-semibold text-slate-900 dark:text-slate-100">
          {{ $t('INBOX_MGMT.WACALLS_SETTINGS.TITLE') }}
        </h3>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-xs text-slate-400">{{ wacallsUrl }}</span>
        <button
          class="text-sm font-medium text-woot-500 hover:text-woot-600 transition-colors"
          :disabled="loading"
          @click="fetchSessions"
        >
          {{ $t('INBOX_MGMT.WACALLS_SETTINGS.REFRESH') }}
        </button>
        <button
          class="px-3 py-1.5 text-sm font-medium text-white bg-woot-500 rounded-md hover:bg-woot-600 transition-colors"
          @click="showAddForm = !showAddForm"
        >
          {{ $t('INBOX_MGMT.WACALLS_SETTINGS.NEW_SESSION') }}
        </button>
      </div>
    </div>

    <div
      v-if="error"
      class="px-5 py-3 bg-red-50 dark:bg-red-900/20 text-sm text-red-600"
    >
      {{ error }}
    </div>

    <div
      v-if="showAddForm"
      class="px-5 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50"
    >
      <form class="flex gap-2 items-end" @submit.prevent="createSession">
        <div class="flex-1">
          <label
            class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-400"
          >
            {{ $t('INBOX_MGMT.WACALLS_SETTINGS.SESSION_NAME') }}
          </label>
          <input
            v-model="newSessionName"
            type="text"
            required
            :placeholder="
              $t('INBOX_MGMT.WACALLS_SETTINGS.SESSION_NAME_PLACEHOLDER')
            "
            class="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
          />
        </div>
        <button
          type="submit"
          :disabled="actionLoading"
          class="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
        >
          {{ $t('INBOX_MGMT.WACALLS_SETTINGS.CREATE') }}
        </button>
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 dark:bg-slate-700 dark:text-slate-300 rounded-md hover:bg-slate-200 transition-colors"
          @click="showAddForm = false"
        >
          {{ $t('GENERAL.CANCEL') }}
        </button>
      </form>
    </div>

    <div
      v-if="loading && !sessions.length"
      class="flex items-center justify-center py-12 gap-3 text-slate-500"
    >
      <svg class="size-5 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        />
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
        />
      </svg>
      <span class="text-sm">{{
        $t('INBOX_MGMT.WACALLS_SETTINGS.LOADING')
      }}</span>
    </div>

    <div v-else-if="!visibleSessions.length" class="text-center py-12 px-4">
      <svg
        viewBox="0 0 24 24"
        class="size-10 mx-auto mb-3 text-slate-300"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
      >
        <path
          d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.8 1.24h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 6 6l.96-.96a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"
        />
      </svg>
      <p class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
        {{ $t('INBOX_MGMT.WACALLS_SETTINGS.NO_SESSIONS') }}
      </p>
      <p class="text-xs text-slate-500">
        {{ $t('INBOX_MGMT.WACALLS_SETTINGS.NO_SESSIONS_HINT') }}
      </p>
    </div>

    <ul v-else class="divide-y divide-slate-100 dark:divide-slate-700">
      <li
        v-for="session in visibleSessions"
        :key="session.id"
        class="px-5 py-4"
      >
        <div class="flex items-start justify-between gap-4">
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2 flex-wrap">
              <span
                class="text-sm font-semibold text-slate-900 dark:text-slate-100"
              >
                {{ session.name || session.id }}
              </span>
              <span
                class="px-2 py-0.5 text-xs font-medium rounded-full"
                :class="[statusColor(session.state), statusBg(session.state)]"
              >
                {{ session.state || 'unknown' }}
              </span>
              <span
                v-if="session.paired && session.jid"
                class="text-xs text-slate-400"
              >
                {{ session.jid }}
              </span>
            </div>
            <div
              v-if="chatwootConfigs[session.id]?.enabled"
              class="mt-2 flex items-center gap-1.5"
            >
              <span class="inline-block size-2 rounded-full bg-green-500" />
              <span class="text-xs text-slate-500">
                {{
                  $t('INBOX_MGMT.WACALLS_SETTINGS.CHATWOOT_INBOX_N', {
                    id: chatwootConfigs[session.id]?.chatwoot?.inbox_id,
                  })
                }}
              </span>
              <span
                v-if="chatwootConfigs[session.id]?.webhook_url"
                class="text-xs text-slate-400 truncate max-w-xs"
              >
                {{ $t('INBOX_MGMT.WACALLS_SETTINGS.WEBHOOK_SET') }}
              </span>
            </div>

            <div
              v-if="session.state === 'qr' || qrData[session.id]"
              class="mt-3"
            >
              <p
                v-if="session.state === 'qr' && !qrData[session.id]"
                class="text-xs text-blue-600 dark:text-blue-400 animate-pulse"
              >
                {{ $t('INBOX_MGMT.WACALLS_SETTINGS.GENERATING_QR') }}
              </p>
            </div>

            <Teleport v-if="qrData[session.id]" to="body">
              <div
                class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
                @click.self="qrData[session.id] = null"
              >
                <div
                  class="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 flex flex-col items-center gap-4 max-w-sm w-full mx-4"
                >
                  <h3
                    class="text-lg font-bold text-slate-900 dark:text-slate-50"
                  >
                    {{
                      $t('INBOX_MGMT.WACALLS_SETTINGS.PAIR_TITLE', {
                        name: session.name,
                      })
                    }}
                  </h3>
                  <p
                    class="text-sm text-slate-500 dark:text-slate-400 text-center"
                  >
                    {{ $t('INBOX_MGMT.WACALLS_SETTINGS.PAIR_HINT') }}
                  </p>
                  <canvas
                    :id="'qr-' + session.id"
                    class="border rounded-xl size-[220px]"
                  />
                  <button
                    class="mt-2 px-4 py-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
                    @click="qrData[session.id] = null"
                  >
                    {{ $t('INBOX_MGMT.WACALLS_SETTINGS.CLOSE') }}
                  </button>
                </div>
              </div>
            </Teleport>
          </div>
          <div class="flex items-center gap-2 flex-shrink-0">
            <button
              class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
              :class="
                chatwootConfigs[session.id]?.enabled
                  ? 'text-green-700 bg-green-50 dark:bg-green-900/20 hover:bg-green-100'
                  : 'text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200'
              "
              :disabled="actionLoading"
              @click="autoConfigureChatwoot(session.id)"
            >
              {{
                chatwootConfigs[session.id]?.enabled
                  ? $t('INBOX_MGMT.WACALLS_SETTINGS.CHATWOOT_CONNECTED')
                  : $t('INBOX_MGMT.WACALLS_SETTINGS.CONNECT_CHATWOOT')
              }}
            </button>
            <button
              v-if="session.state !== 'open'"
              class="px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors flex items-center gap-1"
              :class="
                session.state === 'qr'
                  ? 'bg-green-600 hover:bg-green-700 animate-pulse'
                  : session.state === 'logged_out'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-blue-600 hover:bg-blue-700'
              "
              :disabled="actionLoading"
              @click="pairSession(session.id)"
            >
              <span v-if="session.state === 'qr'">{{
                $t('INBOX_MGMT.WACALLS_SETTINGS.VIEW_QR')
              }}</span>
              <span v-else-if="session.state === 'logged_out'">{{
                $t('INBOX_MGMT.WACALLS_SETTINGS.REACTIVATE')
              }}</span>
              <span v-else>{{
                $t('INBOX_MGMT.WACALLS_SETTINGS.START_QR')
              }}</span>
            </button>
            <button
              v-if="session.state === 'open'"
              class="px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 rounded-md hover:bg-slate-200 transition-colors"
              :disabled="actionLoading"
              @click="logoutSession(session.id)"
            >
              {{ $t('INBOX_MGMT.WACALLS_SETTINGS.LOGOUT') }}
            </button>
            <button
              class="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 dark:bg-red-900/20 rounded-md hover:bg-red-100 transition-colors"
              :disabled="actionLoading"
              @click="deleteSession(session.id)"
            >
              {{ $t('INBOX_MGMT.WACALLS_SETTINGS.DELETE') }}
            </button>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>
